import React, { useMemo } from 'react'
import {
  buildDatasetGroups,
  splitPublicationsByRelease,
  RELEASE_SECTIONS,
  resolvePublicationForDataset,
  getSidebarRowDisplayLabel,
  isDatasetRowActive
} from './DatasetTreeLeft'
import { DatasetTreeLeftWrapper } from './DownloadData.styles'

/** Get publication id */
function getPublicationId(pub) {
  return pub?.publicationId ?? pub?.id ?? null
}

function ReleaseSection({
  section,
  datasetGroups,
  activeDatasetId,
  activePublicationId,
  publicationByDataset,
  onSelectPublication,
  onDatasetInfoClick
}) {
  if (!datasetGroups.length) return null

  return (
    <div className="release-section" data-section={section.key}>
      <h3 className="release-section-heading">{section.heading}</h3>
      <div className="dataset-tree dataset-tree-internal" role="tree" aria-label={`Datasets – ${section.heading}`}>
        {datasetGroups.map((group) => {
          const pubs = group.publications || []
          if (!pubs.length) return null

          const storedSelection = publicationByDataset?.[group.datasetId]
          const rowSelection = resolvePublicationForDataset(group, storedSelection, false)
          if (!rowSelection) return null

          const pubForRow = pubs.find(
            (p) => String(getPublicationId(p)) === String(rowSelection.publicationId)
          ) ?? pubs[0]
          const isSelected = isDatasetRowActive(group, activeDatasetId, activePublicationId, pubs)
          const datasetDisplayName = getSidebarRowDisplayLabel(pubForRow)

          return (
            <div key={group.datasetId} className="dataset-group dataset-group-internal">
              <div className="dataset-group-internal-row-wrap">
                <button
                  type="button"
                  className={`dataset-item dataset-preselected-row ${isSelected ? 'selected' : ''}`}
                  onClick={() => {
                    onSelectPublication(rowSelection)
                  }}
                  aria-pressed={isSelected}
                  aria-label={`Select ${datasetDisplayName}`}
                >
                  {datasetDisplayName}
                </button>
                {onDatasetInfoClick && (
                  <button
                    type="button"
                    className="dataset-info-link dataset-info-link-internal"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      onDatasetInfoClick(pubForRow, group.datasetId)
                    }}
                    aria-label="Dataset information"
                    title="Dataset information"
                  >
                    <span className="dataset-info-icon" aria-hidden>
                      <svg xmlns="http://www.w3.org/2000/svg" height="14" width="14" viewBox="0 0 16 16">
                        <g fill="currentColor" stroke="currentColor">
                          <circle data-color="color-2" data-stroke="none" fill="currentColor" cx="8.5" cy="5" r="1" stroke="none" />
                          <circle fill="none" stroke="#333333" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" cx="8.5" cy="8.5" r="7" />
                          <line data-color="color-2" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" x1="8.5" y1="12.5" x2="8.5" y2="8.5" />
                        </g>
                      </svg>
                    </span>
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const DatasetTreeLeftInternal = ({
  datasets = [],
  allPublications = [],
  activeDatasetId,
  activePublicationId,
  publicationByDataset = {},
  onSelectPublication,
  onDatasetInfoClick
}) => {
  const { latest: latestPubs, previous: previousPubs } = useMemo(
    () => splitPublicationsByRelease(allPublications),
    [allPublications]
  )

  const latestGroups = useMemo(
    () => buildDatasetGroups(datasets, latestPubs),
    [datasets, latestPubs]
  )
  const previousGroups = useMemo(
    () => buildDatasetGroups(datasets, previousPubs),
    [datasets, previousPubs]
  )

  const hasData =
    latestGroups.length > 0 ||
    previousGroups.length > 0 ||
    (Array.isArray(datasets) && datasets.length > 0) ||
    (Array.isArray(allPublications) && allPublications.length > 0)

  if (!hasData) {
    return (
      <DatasetTreeLeftWrapper>
        <div className="dataset-tree-loading">Loading datasets…</div>
      </DatasetTreeLeftWrapper>
    )
  }

  return (
    <DatasetTreeLeftWrapper>
      <div className="dataset-tree-internal-wrap">
        {RELEASE_SECTIONS.map((section) => {
          const groups = section.key === 'latest' ? latestGroups : previousGroups
          return (
            <ReleaseSection
              key={section.key}
              section={section}
              datasetGroups={groups}
              activeDatasetId={activeDatasetId}
              activePublicationId={activePublicationId}
              publicationByDataset={publicationByDataset}
              onSelectPublication={onSelectPublication}
              onDatasetInfoClick={onDatasetInfoClick}
            />
          )
        })}
      </div>
    </DatasetTreeLeftWrapper>
  )
}

export default DatasetTreeLeftInternal
